import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Database,
  Server,
  Cloud,
  GitBranch,
  Shield,
  Zap,
  Code,
  Settings,
  Monitor,
  Cpu,
  HardDrive,
  Network,
  Lock,
  CheckCircle,
  AlertTriangle,
  Info,
  BookOpen
} from 'lucide-react';

export const Information: React.FC = () => {
  const architectureLayers = [
    {
      name: 'Frontend Layer',
      description: 'React-based user interface with TypeScript',
      technologies: ['React 18', 'TypeScript', 'Tailwind CSS', 'Lucide React'],
      icon: Monitor,
      color: 'primary',
      details: [
        'Single Page Application (SPA) architecture',
        'Component-based design system',
        'Responsive design with mobile-first approach',
        'Client-side routing with React Router',
        'State management with React Context API'
      ]
    },
    {
      name: 'Application Layer',
      description: 'Business logic and data processing',
      technologies: ['Node.js', 'Express.js', 'TypeScript'],
      icon: Cpu,
      color: 'sage',
      details: [
        'RESTful API endpoints',
        'Authentication and authorization',
        'Data validation and sanitization',
        'Business logic processing',
        'Integration with external services'
      ]
    },
    {
      name: 'Data Layer',
      description: 'Data storage and management',
      technologies: ['PostgreSQL', 'Redis', 'File System'],
      icon: Database,
      color: 'amber',
      details: [
        'Relational database for structured data',
        'In-memory caching with Redis',
        'File-based storage for assets',
        'Data backup and recovery',
        'Database migrations and versioning'
      ]
    },
    {
      name: 'Infrastructure Layer',
      description: 'Deployment and hosting infrastructure',
      technologies: ['Render', 'Docker', 'CDN', 'SSL'],
      icon: Server,
      color: 'coral',
      details: [
        'Cloud hosting on Render platform',
        'Containerized deployment with Docker',
        'Content Delivery Network (CDN)',
        'SSL/TLS encryption',
        'Auto-scaling and load balancing'
      ]
    }
  ];

  const deploymentStages = [
    {
      stage: 'Development',
      description: 'Local development environment',
      tools: ['VS Code', 'Git', 'Node.js', 'npm/yarn'],
      icon: Code,
      color: 'primary'
    },
    {
      stage: 'Staging',
      description: 'Testing and validation environment',
      tools: ['GitHub Actions', 'Render Preview', 'Testing Suite'],
      icon: GitBranch,
      color: 'sage'
    },
    {
      stage: 'Production',
      description: 'Live production environment',
      tools: ['Render Production', 'Custom Domain', 'Monitoring'],
      icon: Cloud,
      color: 'amber'
    }
  ];

  const securityFeatures = [
    {
      feature: 'Authentication',
      description: 'Secure user authentication and session management',
      icon: Lock,
      implementation: 'Clerk authentication service with JWT tokens'
    },
    {
      feature: 'Data Encryption',
      description: 'End-to-end encryption for sensitive data',
      icon: Shield,
      implementation: 'HTTPS/TLS encryption in transit, AES-256 at rest'
    },
    {
      feature: 'Access Control',
      description: 'Role-based access control and permissions',
      icon: CheckCircle,
      implementation: 'Granular permissions system with user roles'
    },
    {
      feature: 'Monitoring',
      description: 'Real-time monitoring and alerting',
      icon: AlertTriangle,
      implementation: 'Application performance monitoring and error tracking'
    }
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      primary: {
        bg: 'bg-consulting-slate-blue-50',
        border: 'border-consulting-slate-blue-200',
        text: 'text-consulting-slate-blue-900',
        accent: 'text-consulting-slate-blue-600',
        badge: 'bg-consulting-slate-blue-100 text-consulting-slate-blue-800'
      },
      sage: {
        bg: 'bg-sage-50',
        border: 'border-sage-200',
        text: 'text-sage-900',
        accent: 'text-sage-600',
        badge: 'bg-sage-100 text-sage-800'
      },
      amber: {
        bg: 'bg-[var(--mustard-dim)]',
        border: 'border-[var(--mustard)]',
        text: 'text-[var(--mustard)]',
        accent: 'text-[var(--mustard)]',
        badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
      },
      coral: {
        bg: 'bg-coral-50',
        border: 'border-coral-200',
        text: 'text-coral-900',
        accent: 'text-coral-600',
        badge: 'bg-coral-100 text-coral-800'
      }
    };
    return colors[color as keyof typeof colors] || colors.primary;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <Link to="/elevation/home" className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to Overview
        </Link>
        <div className="flex items-center gap-3 mb-4">
          <div className="px-4 py-2 font-bold text-xl bg-consulting-slate-blue-100 text-consulting-slate-blue-800">
            Information
          </div>
          <BookOpen className="h-10 w-10 text-consulting-slate-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-charcoal-900">Detailed Information</h1>
            <p className="text-charcoal-600">Comprehensive technical and system information</p>
          </div>
        </div>
        <p className="text-lg text-charcoal-700">
          This page provides detailed technical information about Peak Priorities Base Camp, 
          including system architecture, deployment strategy, security measures, and implementation details.
        </p>
      </div>

      {/* Architecture Overview */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 p-8 border-2 border-consulting-slate-blue-200">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">System Architecture</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {architectureLayers.map((layer, index) => {
            const Icon = layer.icon;
            const colors = getColorClasses(layer.color);
            return (
              <div key={index} className={`${colors.bg} ${colors.border} border-2  p-6`}>
                <div className="flex items-center gap-3 mb-4">
                  <Icon className={`h-8 w-8 ${colors.accent}`} />
                  <h3 className={`text-lg font-bold ${colors.text}`}>{layer.name}</h3>
                </div>
                <p className={`text-sm ${colors.text} mb-4`}>{layer.description}</p>
                <div className="space-y-2">
                  <h4 className={`text-sm font-semibold ${colors.text}`}>Technologies:</h4>
                  <div className="flex flex-wrap gap-1">
                    {layer.technologies.map((tech, techIndex) => (
                      <span key={techIndex} className={`px-2 py-1 text-xs rounded ${colors.badge}`}>
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                <details className="mt-4">
                  <summary className={`text-sm font-medium ${colors.text} cursor-pointer hover:underline`}>
                    View Details
                  </summary>
                  <ul className="mt-2 space-y-1">
                    {layer.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className={`text-xs ${colors.text} flex items-start gap-2`}>
                        <CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
            );
          })}
        </div>
      </div>

      {/* Current Implementation */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Current Implementation</h2>
        <p className="text-charcoal-700 mb-6">
          This demo application showcases Peak Priorities Base Camp implemented 
          as a comprehensive Payload CMS collection with a React-based exploration interface.
        </p>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-6">
            <h3 className="text-xl font-semibold text-consulting-slate-blue-900 mb-4 flex items-center gap-2">
              <Server className="h-6 w-6" />
              Backend (Payload CMS)
            </h3>
            <ul className="space-y-2 text-consulting-slate-blue-800">
              <li className="flex items-start space-x-2">
                <CheckCircle className="h-4 w-4 text-consulting-slate-blue-600 mt-1 flex-shrink-0" />
                <span>Complete Master Registry collection schema</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="h-4 w-4 text-consulting-slate-blue-600 mt-1 flex-shrink-0" />
                <span>Four-layer meta-stack implementation</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="h-4 w-4 text-consulting-slate-blue-600 mt-1 flex-shrink-0" />
                <span>Relationship mapping and system integration</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="h-4 w-4 text-consulting-slate-blue-600 mt-1 flex-shrink-0" />
                <span>RESTful API with GraphQL support</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-sage-50 border-2 border-sage-200 p-6">
            <h3 className="text-xl font-semibold text-sage-900 mb-4 flex items-center gap-2">
              <Monitor className="h-6 w-6" />
              Frontend (React)
            </h3>
            <ul className="space-y-2 text-sage-800">
              <li className="flex items-start space-x-2">
                <CheckCircle className="h-4 w-4 text-sage-600 mt-1 flex-shrink-0" />
                <span>Interactive registry explorer with search and filtering</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="h-4 w-4 text-sage-600 mt-1 flex-shrink-0" />
                <span>Meta-stack layer visualization</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="h-4 w-4 text-sage-600 mt-1 flex-shrink-0" />
                <span>Detailed entry views with full meta-stack data</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="h-4 w-4 text-sage-600 mt-1 flex-shrink-0" />
                <span>Responsive design with Tailwind CSS</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Deployment Pipeline */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Deployment Pipeline</h2>
        <p className="text-charcoal-700 mb-6">
          Our deployment process follows a three-stage pipeline ensuring quality and reliability at every step.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {deploymentStages.map((stage, index) => {
            const Icon = stage.icon;
            const colors = getColorClasses(stage.color);
            return (
              <div key={index} className={`${colors.bg} ${colors.border} border-2  p-6`}>
                <div className="flex items-center gap-3 mb-4">
                  <Icon className={`h-8 w-8 ${colors.accent}`} />
                  <h3 className={`text-lg font-bold ${colors.text}`}>{stage.stage}</h3>
                </div>
                <p className={`text-sm ${colors.text} mb-4`}>{stage.description}</p>
                <div className="space-y-2">
                  <h4 className={`text-sm font-semibold ${colors.text}`}>Tools & Services:</h4>
                  <div className="flex flex-wrap gap-1">
                    {stage.tools.map((tool, toolIndex) => (
                      <span key={toolIndex} className={`px-2 py-1 text-xs rounded ${colors.badge}`}>
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Security & Compliance */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Security & Compliance</h2>
        <p className="text-charcoal-700 mb-6">
          Security is a top priority. We implement multiple layers of security measures to protect user data and ensure system integrity.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {securityFeatures.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div key={index} className="border border-[var(--rule)] p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Icon className="h-6 w-6 text-consulting-slate-blue-600" />
                  <h3 className="text-lg font-semibold text-charcoal-900">{feature.feature}</h3>
                </div>
                <p className="text-charcoal-700 mb-3">{feature.description}</p>
                <div className="bg-[var(--surface)] p-3">
                  <p className="text-sm text-charcoal-600">
                    <strong>Implementation:</strong> {feature.implementation}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Performance & Scalability */}
      <div className="bg-gradient-to-r from-sage-50 to-amber-50 p-8 border-2 border-sage-200">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Performance & Scalability</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4 flex items-center gap-2">
              <Zap className="h-5 w-5 text-[var(--mustard)]" />
              Performance Optimizations
            </h3>
            <ul className="space-y-2 text-charcoal-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-sage-600 mt-0.5 flex-shrink-0" />
                <span>Code splitting and lazy loading for faster initial page loads</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-sage-600 mt-0.5 flex-shrink-0" />
                <span>Image optimization and compression</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-sage-600 mt-0.5 flex-shrink-0" />
                <span>Database query optimization and indexing</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-sage-600 mt-0.5 flex-shrink-0" />
                <span>CDN integration for global content delivery</span>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4 flex items-center gap-2">
              <Network className="h-5 w-5 text-sage-600" />
              Scalability Features
            </h3>
            <ul className="space-y-2 text-charcoal-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
                <span>Horizontal scaling with load balancing</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
                <span>Auto-scaling based on demand</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
                <span>Microservices architecture for independent scaling</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
                <span>Caching strategies for improved performance</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Technology Stack */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Technology Stack</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-6">
            <h3 className="text-lg font-semibold text-consulting-slate-blue-900 mb-3 flex items-center gap-2">
              <Monitor className="h-5 w-5" />
              Frontend
            </h3>
            <ul className="space-y-1 text-sm text-consulting-slate-blue-800">
              <li>• React 18 with TypeScript</li>
              <li>• Tailwind CSS for styling</li>
              <li>• Lucide React for icons</li>
              <li>• React Router for navigation</li>
              <li>• Context API for state management</li>
            </ul>
          </div>
          <div className="bg-sage-50 border-2 border-sage-200 p-6">
            <h3 className="text-lg font-semibold text-sage-900 mb-3 flex items-center gap-2">
              <Server className="h-5 w-5" />
              Backend
            </h3>
            <ul className="space-y-1 text-sm text-sage-800">
              <li>• Node.js with Express.js</li>
              <li>• TypeScript for type safety</li>
              <li>• PostgreSQL database</li>
              <li>• Redis for caching</li>
              <li>• Clerk for authentication</li>
            </ul>
          </div>
          <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-6">
            <h3 className="text-lg font-semibold text-[var(--mustard)] mb-3 flex items-center gap-2">
              <Cloud className="h-5 w-5" />
              Infrastructure
            </h3>
            <ul className="space-y-1 text-sm text-[var(--mustard)]">
              <li>• Render for hosting</li>
              <li>• Docker for containerization</li>
              <li>• GitHub for version control</li>
              <li>• GitHub Actions for CI/CD</li>
              <li>• Custom domain with SSL</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Monitoring & Maintenance */}
      <div className="bg-gradient-to-r from-coral-50 to-consulting-slate-blue-50 p-8 border-2 border-coral-200">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Monitoring & Maintenance</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4 flex items-center gap-2">
              <Settings className="h-5 w-5 text-coral-600" />
              System Monitoring
            </h3>
            <ul className="space-y-2 text-charcoal-700">
              <li className="flex items-start gap-2">
                <Info className="h-4 w-4 text-coral-600 mt-0.5 flex-shrink-0" />
                <span>Real-time performance metrics and alerts</span>
              </li>
              <li className="flex items-start gap-2">
                <Info className="h-4 w-4 text-coral-600 mt-0.5 flex-shrink-0" />
                <span>Error tracking and logging</span>
              </li>
              <li className="flex items-start gap-2">
                <Info className="h-4 w-4 text-coral-600 mt-0.5 flex-shrink-0" />
                <span>Uptime monitoring and health checks</span>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4 flex items-center gap-2">
              <HardDrive className="h-5 w-5 text-consulting-slate-blue-600" />
              Maintenance
            </h3>
            <ul className="space-y-2 text-charcoal-700">
              <li className="flex items-start gap-2">
                <Info className="h-4 w-4 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                <span>Automated backups and disaster recovery</span>
              </li>
              <li className="flex items-start gap-2">
                <Info className="h-4 w-4 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                <span>Regular security updates and patches</span>
              </li>
              <li className="flex items-start gap-2">
                <Info className="h-4 w-4 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                <span>Performance optimization and tuning</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
